import primaryPaletteCss from '../src/styles/primary.css?inline';

(function () {
  let iframe = null;
  let container = null;
  let toggleButton = null;
  let isDirectChat = false;
  let origin = '*';
  let isReady = false;
  let commandQueue = [];
  let previewHost = null;
  let videoPreviewHost = null;
  let isAllowedOrigin = false;
  let pendingFocusInputRaf = null;
  let focusListenersRegistered = false;

  const VISITOR_ID_COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;
  const CONVERSATION_ACCESS_TOKEN_COOKIE_MAX_AGE_SECONDS = 3 * 24 * 60 * 60;
  const EMPTY_CONVERSATION_ACCESS_TOKEN_VALUE = '__CHAT_EMPTY_CONVERSATION_ACCESS_TOKEN_VALUE__';
  const CHAT_RELOAD_MESSAGE_TYPE = 'CHAT_RELOAD';
  const CHAT_SIZE = {
    width: '22.5rem',
    height: '38.75rem',
  };
  const script = document.currentScript;

  const isRecord = value => {
    return typeof value === 'object' && value !== null;
  };

  const getCookieMessagePayloadKey = payload => {
    if (!isRecord(payload) || typeof payload.key !== 'string') return '';

    return payload.key.trim();
  };

  const getCookieMessagePayloadValue = payload => {
    if (!isRecord(payload) || typeof payload.value !== 'string') return '';

    return payload.value.trim();
  };

  const getCookie = name => {
    const encodedName = encodeURIComponent(name);
    const cookie = document.cookie.split('; ').find(row => row.startsWith(`${encodedName}=`));

    if (!cookie) return null;

    const value = cookie.slice(encodedName.length + 1);

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const setCookie = (cookieName, value, maxAgeSeconds, sameSite = 'Lax') => {
    document.cookie = [
      `${encodeURIComponent(cookieName)}=${encodeURIComponent(value)}`,
      'path=/',
      `max-age=${maxAgeSeconds}`,
      `SameSite=${sameSite}`,
      sameSite === 'None' || location.protocol === 'https:' ? 'Secure' : '',
    ]
      .filter(Boolean)
      .join('; ');

    return getCookie(cookieName) === value;
  };

  const deleteCookie = cookieName => {
    const encodedName = encodeURIComponent(cookieName);

    document.cookie = `${encodedName}=; path=/; max-age=0; SameSite=None; Secure`;
    document.cookie = `${encodedName}=; path=/; max-age=0; SameSite=Lax`;

    return getCookie(cookieName) === null;
  };

  const setVisitorIdCookie = (cookieName, visitorId, sameSite = 'Lax') => {
    return setCookie(cookieName, visitorId, VISITOR_ID_COOKIE_MAX_AGE_SECONDS, sameSite);
  };

  const setConversationAccessTokenCookie = (cookieName, conversationAccessToken, sameSite = 'Lax') => {
    return setCookie(cookieName, conversationAccessToken, CONVERSATION_ACCESS_TOKEN_COOKIE_MAX_AGE_SECONDS, sameSite);
  };

  const persistVisitorId = (cookieName, visitorId) => {
    const sameSite = location.protocol === 'https:' ? 'None' : 'Lax';
    return setVisitorIdCookie(cookieName, visitorId, sameSite);
  };

  const persistConversationAccessToken = (cookieName, conversationAccessToken) => {
    const sameSite = location.protocol === 'https:' ? 'None' : 'Lax';
    return setConversationAccessTokenCookie(cookieName, conversationAccessToken, sameSite);
  };

  function isDomainAllowed(allowedDomains) {
    if (!allowedDomains) return false;
    if (typeof window === 'undefined') return false;

    const normalize = domain => {
      return domain
        .toLowerCase()
        .trim()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/$/, '');
    };

    const currentDomain = normalize(window.location.origin);

    return allowedDomains.some(domain => {
      const normalized = normalize(domain);

      return currentDomain === normalized || currentDomain.endsWith(`.${normalized}`);
    });
  }

  const fetchBrandConfig = async ({ url, brandId, tenantId }) => {
    const response = await fetch(`${url}/public/v1/brands/${brandId}`, {
      method: 'GET',
      headers: { 'x-tenant-id': tenantId },
    });

    if (!response.ok) throw new Error(`Failed to fetch brand config: ${response.status}`);
    return response.json();
  };

  const decodeHtml = value =>
    value
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');

  const getScriptParams = params => {
    if (!params.length || script == null) return [];
    const list = [];
    params.forEach(param => {
      if (!isRecord(param)) return;
      const paramName = param.paramName?.trim() || '';
      const value = paramName ? script?.getAttribute(`data-${paramName}`)?.trim() : '';
      if (value) list.push({ target: param.target, type: param.type, value: decodeHtml(value) });
    });

    return list;
  };

  const toRem = value => {
    const numericValue = Number.parseFloat(value);
    const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
    return `${safeValue / 16}rem`;
  };

  const getPositionStyles = (position, sideSpacing = 0, bottomSpacing = 0) => {
    if (isDirectChat) {
      return `
        bottom: 0;
        ${position === 'left' ? 'left : 0' : 'right :0'};`;
    }

    const sideOffset = `calc(${toRem(sideSpacing)} + env(safe-area-inset-${position === 'left' ? 'left' : 'right'}, 0rem))`;
    const bottomOffset = `calc(${toRem(bottomSpacing)} + env(safe-area-inset-bottom, 0rem))`;
    const styles = {
      right: `bottom:${bottomOffset};right:${sideOffset};`,
      left: `bottom:${bottomOffset};left:${sideOffset};`,
    };
    return styles[position] || styles.right;
  };

  const getIframeStyles = (sideSpacing = 0, bottomSpacing = 0) => {
    if (isDirectChat)
      return `
        position: absolute; 
        display: block;
        width: 100%;
        height: 100%;
      `;

    const sideOffset = toRem(sideSpacing);
    const bottomOffset = toRem(bottomSpacing);
    return `
      position: fixed; 
      display: none;
      width: ${CHAT_SIZE.width};
      height: ${CHAT_SIZE.height};
      max-width: calc(100vw - (${sideOffset} * 2) - env(safe-area-inset-left, 0rem) - env(safe-area-inset-right, 0rem));
      max-height: calc(100dvh - ${bottomOffset} - env(safe-area-inset-top, 0rem) - env(safe-area-inset-bottom, 0rem));
    `;
  };

  const isHostMobileViewport = () => {
    if (typeof window.matchMedia === 'function') {
      return !window.matchMedia('(min-width: 48rem)').matches;
    }

    return window.innerWidth < 768;
  };

  const reloadIframe = () => {
    if (!iframe) {
      window.location.reload();
      return;
    }

    isReady = false;
    commandQueue = [];
    closeSDK();
    iframe.src = iframe.src;
  };

  const safePostMessage = data => {
    if (!iframe?.contentWindow || !origin) return;
    iframe.contentWindow.postMessage(data, origin);
  };

  const sendCookieMessageResponse = (type, payload) => {
    safePostMessage({ type, payload });
  };

  const createCookieMessageBridge = ({ getType, getResponseType, updateType, updateResponseType, persistValue }) => ({
    handle: (type, payload) => {
      if (type === getType) {
        const key = getCookieMessagePayloadKey(payload);
        sendCookieMessageResponse(getResponseType, key ? getCookie(key) : null);
        return true;
      }

      if (type !== updateType) return false;

      const key = getCookieMessagePayloadKey(payload);
      const value = getCookieMessagePayloadValue(payload);

      if (!key || !value) {
        sendCookieMessageResponse(updateResponseType, false);
        return true;
      }

      try {
        sendCookieMessageResponse(updateResponseType, persistValue(key, value));
      } catch {
        sendCookieMessageResponse(updateResponseType, false);
      }

      return true;
    },
  });

  const persistConversationAccessTokenMessageValue = (key, value) =>
    value === EMPTY_CONVERSATION_ACCESS_TOKEN_VALUE ? deleteCookie(key) : persistConversationAccessToken(key, value);

  const cookieMessageBridges = [
    createCookieMessageBridge({
      getType: 'GET_VISITOR_ID',
      getResponseType: 'GET_VISITOR_ID_RESPONSE',
      updateType: 'UPDATE_VISITOR_ID',
      updateResponseType: 'UPDATE_VISITOR_ID_RESPONSE',
      persistValue: persistVisitorId,
    }),
    createCookieMessageBridge({
      getType: 'GET_CONVERSATION_ACCESS_TOKEN',
      getResponseType: 'GET_CONVERSATION_ACCESS_TOKEN_RESPONSE',
      updateType: 'UPDATE_CONVERSATION_ACCESS_TOKEN',
      updateResponseType: 'UPDATE_CONVERSATION_ACCESS_TOKEN_RESPONSE',
      persistValue: persistConversationAccessTokenMessageValue,
    }),
  ];

  const flushQueue = () => {
    commandQueue.forEach(fn => fn());
    commandQueue = [];
  };

  const openSDK = () => {
    if (!iframe) return;
    iframe.style.display = 'block';
    toggleButton?.setAttribute('aria-label', 'Close chat');
    pendingFocusInputRaf = requestAnimationFrame(() => {
      pendingFocusInputRaf = null;
      safePostMessage({ type: 'CHAT_FOCUS_INPUT' });
    });
  };

  const closeSDK = () => {
    if (!iframe || isDirectChat) return;
    if (pendingFocusInputRaf !== null) {
      cancelAnimationFrame(pendingFocusInputRaf);
      pendingFocusInputRaf = null;
    }
    safePostMessage({ type: 'CHAT_HIDDEN' });
    iframe.style.display = 'none';
    toggleButton?.setAttribute('aria-label', 'Open chat');
  };

  const createImagePreview = (imageUrl, primaryColor) => {
    if (previewHost) return;

    previewHost = document.createElement('div');
    previewHost.id = 'chat-sdk-image-preview';
    if (typeof primaryColor === 'string' && CSS.supports('color', primaryColor)) {
      previewHost.style.setProperty('--primary-color', primaryColor);
    }
    const shadow = previewHost.attachShadow({ mode: 'closed' });

    shadow.innerHTML = `
      <style>
        ${primaryPaletteCss}
        :host {
          position: fixed;
          inset: 0;
          z-index: 99999;
        }
        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.2s ease-out;
          cursor: zoom-out;
        }
        .overlay img {
          max-width: 90vw;
          max-height: 90vh;
          object-fit: contain;
          box-sizing: border-box;
          border: 2px solid var(--primary-500);
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
          animation: scaleIn 0.2s ease-out;
          cursor: default;
        }
        .close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid var(--primary-400);
          background: rgba(255, 255, 255, 0.15);
          color: #fff;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(4px);
          transition: background 0.2s;
        }
        .close-btn:hover {
          background: var(--primary-700);
        }
        .close-btn:focus-visible {
          outline: 3px solid var(--primary-400);
          outline-offset: 2px;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.92); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      </style>
      <div class="overlay">
        <img src="${imageUrl}" alt="preview" />
        <button class="close-btn" aria-label="Close preview">&times;</button>
      </div>
    `;

    const destroy = () => {
      if (previewHost?.parentNode) {
        previewHost.remove();
      }
      previewHost = null;
    };

    shadow.querySelector('.overlay').addEventListener('click', e => {
      if (e.target === e.currentTarget) destroy();
    });
    shadow.querySelector('.close-btn').addEventListener('click', destroy);

    document.body.appendChild(previewHost);
  };

  const escapeHtml = str => {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  };

  const createVideoPreview = (videoUrl, fileName, primaryColor) => {
    if (videoPreviewHost) return;

    videoPreviewHost = document.createElement('div');
    videoPreviewHost.id = 'chat-sdk-video-preview';
    if (typeof primaryColor === 'string' && CSS.supports('color', primaryColor)) {
      videoPreviewHost.style.setProperty('--primary-color', primaryColor);
    }
    const shadow = videoPreviewHost.attachShadow({ mode: 'closed' });

    shadow.innerHTML = `
      <style>
        ${primaryPaletteCss}
        :host {
          position: fixed;
          inset: 0;
          z-index: 99999;
        }
        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.2s ease-out;
        }
        .video-container {
          max-width: 90vw;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          animation: scaleIn 0.2s ease-out;
        }
        .video-container video {
          box-sizing: border-box;
          border: 2px solid var(--primary-500);
          accent-color: var(--primary-500);
          max-width: 90vw;
          max-height: 80vh;
          border-radius: 8px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
          background: #000;
        }
        .file-name {
          color: rgba(255, 255, 255, 0.7);
          font-size: 14px;
          text-align: center;
          margin-top: 12px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          max-width: 90vw;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid var(--primary-400);
          background: rgba(255, 255, 255, 0.15);
          color: #fff;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(4px);
          transition: background 0.2s;
        }
        .close-btn:hover {
          background: var(--primary-700);
        }
        .close-btn:focus-visible {
          outline: 3px solid var(--primary-400);
          outline-offset: 2px;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.92); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      </style>
      <div class="overlay">
        <div class="video-container">
          <video controls autoplay playsinline></video>
          ${fileName ? `<p class="file-name">${escapeHtml(fileName)}</p>` : ''}
        </div>
        <button class="close-btn" aria-label="Close preview">&times;</button>
      </div>
    `;

    const video = shadow.querySelector('video');
    video.src = videoUrl;

    const destroy = () => {
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      if (videoPreviewHost?.parentNode) {
        videoPreviewHost.remove();
      }
      videoPreviewHost = null;
    };

    shadow.querySelector('.overlay').addEventListener('click', e => {
      if (e.target === e.currentTarget) destroy();
    });
    shadow.querySelector('.close-btn').addEventListener('click', destroy);

    document.body.appendChild(videoPreviewHost);
  };

  const ChatSDK = {
    init: async (options = {}) => {
      const { position, iframeSrc, tenantId, primaryColor, brandId, sideSpacing, bottomSpacing, logo, mode } = options;
      let { theme } = options;

      isDirectChat = mode === 'direct-chat';

      let resolvedLogo = logo;
      let resolvedPrimaryColor = primaryColor;
      let resolvedSideSpacing = sideSpacing;
      let resolvedBottomSpacing = bottomSpacing;
      let resolvedPosition = position || 'right';
      let resolvedParams = [];

      container = document.getElementById('chat-sdk-container');
      if (!container) {
        container = document.createElement('div');
        container.id = 'chat-sdk-container';
        document.body.appendChild(container);
      }
      if (isDirectChat) {
        container.style.cssText = `
          position: relative;
          width: min(${CHAT_SIZE.width}, 100%);
          height: min(${CHAT_SIZE.height}, 96svh);
        `;
      }

      iframe = document.getElementById('chat-sdk-iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'chat-sdk-iframe';
        iframe.src = `${iframeSrc}/`;
        iframe.style.cssText = `
          border: none;
          ${getIframeStyles(resolvedSideSpacing, resolvedBottomSpacing)}
          z-index: 9999;
          border-radius: 16px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        `;
        container.appendChild(iframe);
      }

      const handleMessage = async event => {
        if (event.source !== iframe.contentWindow) return;
        const { type, payload } = event.data;

        if (type === 'IFRAME_READY') {
          try {
            const attachmentUrl = `${payload.apiPath}/asset/public/v1/files?key=`;
            const brand = await fetchBrandConfig({ url: payload.apiPath, brandId, tenantId });
            isAllowedOrigin = isDomainAllowed(brand?.data?.allowedDomains);

            if (!isAllowedOrigin) return;
            const widgetConfig = brand?.data?.widget || {};

            resolvedLogo = widgetConfig?.logo
              ? `${attachmentUrl}${widgetConfig.logo}`
              : `${attachmentUrl}${brand?.data?.logo}`;
            resolvedPrimaryColor = widgetConfig?.primaryColor ?? primaryColor;
            resolvedBottomSpacing = widgetConfig?.bottomSpacing ?? bottomSpacing;
            resolvedSideSpacing = widgetConfig?.sideSpacing ?? sideSpacing;
            resolvedPosition = widgetConfig?.position || resolvedPosition;
            theme = widgetConfig?.theme || options.theme;
            resolvedParams = getScriptParams(brand?.data?.sdkParameters || []);

            if (!isDirectChat) {
              toggleButton = document.getElementById('chat-sdk-toggle');
              if (!toggleButton) {
                toggleButton = document.createElement('button');
                toggleButton.id = 'chat-sdk-toggle';
                toggleButton.innerHTML = `<img src="" alt="Chat" style="width:54px;height:54px;object-fit:cover;border-radius:50%;display:block;">`;
                toggleButton.setAttribute('aria-label', 'Open chat');
                toggleButton.style.cssText = `
                position: fixed;
                visibility: hidden;
                background: #0C0E12; color: white;
                border: none; border-radius: 50%; outline: none;
                width: 54px; height: 54px;
                font-size: 24px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 999;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                transition: all 0.2s;
                padding: 0;
              `;
                toggleButton.onclick = () => {
                  const isVisible = iframe.style.display === 'block';
                  isVisible ? closeSDK() : openSDK();
                };
                if (isAllowedOrigin) {
                  container.appendChild(toggleButton);
                }
              }
            }
          } catch (e) {
            console.warn('[ChatSDK] Could not fetch brand config, using defaults.', e);
          }
          const positionStyle = getPositionStyles(resolvedPosition, resolvedSideSpacing, resolvedBottomSpacing);
          if (!isDirectChat) {
            toggleButton.querySelector('img').src = resolvedLogo;
            toggleButton.style.cssText += positionStyle;
            toggleButton.style.visibility = 'visible';
          }
          iframe.style.cssText += positionStyle;

          isReady = true;
          if (!focusListenersRegistered) {
            focusListenersRegistered = true;
            window.addEventListener('focus', () => safePostMessage({ type: 'WINDOW_FOCUS' }));
            window.addEventListener('blur', () => {
              if (!document.hasFocus()) {
                safePostMessage({ type: 'WINDOW_BLUR' });
              }
            });
          }
          safePostMessage({ type: document.hasFocus() ? 'WINDOW_FOCUS' : 'WINDOW_BLUR' });
          safePostMessage({
            type: 'INIT_PROPS',
            payload: {
              theme,
              position: resolvedPosition,
              tenantId,
              primaryColor: resolvedPrimaryColor,
              brandId,
              logo: resolvedLogo,
              origin: window.location.origin,
              isHostMobile: isHostMobileViewport(),
              prefillParams: resolvedParams,
            },
          });

          flushQueue();
        }
        if (type === 'CHAT_MINIMIZE') {
          closeSDK();
        }
        if (type === CHAT_RELOAD_MESSAGE_TYPE) {
          reloadIframe();
        }
        if (type === 'IMAGE_PREVIEW_OPEN' && payload?.imageUrl) {
          createImagePreview(payload.imageUrl, resolvedPrimaryColor);
        }
        if (type === 'VIDEO_PREVIEW_OPEN' && payload?.videoUrl) {
          createVideoPreview(payload.videoUrl, payload.fileName, resolvedPrimaryColor);
        }
        if (type === 'GET_HOST_URL') {
          safePostMessage({
            type: 'GET_HOST_URL_RESPONSE',
            payload: globalThis.location.href,
            pageTitle: document.title,
          });
        }

        if (cookieMessageBridges.some(bridge => bridge.handle(type, payload))) {
          return;
        }
      };
      window.addEventListener('message', handleMessage);
    },
    toggle: () => {
      const button = document.getElementById('chat-sdk-toggle');

      if (iframe || button) {
        const isVisible = iframe.style.display === 'block';
        isVisible ? closeSDK() : openSDK();
      }
    },
    changeUserInfo: ({ customerName, customerId }) => {
      const send = () => {
        safePostMessage({
          type: 'CHANGE_USER_INFO',
          payload: { customer: { customerName, customerId } },
        });
      };

      if (isReady) {
        send();
      } else {
        commandQueue.push(send);
      }
    },
  };
  globalThis.ChatSDK = ChatSDK;

  if (script) {
    const config = {
      theme: script.dataset.theme,
      position: script.dataset.position,
      tenantId: script.dataset.tenantId,
      iframeSrc: script.dataset.iframeSrc,
      primaryColor: script.dataset.primaryColor,
      brandId: script.dataset.brandId,
      sideSpacing: Number(script.dataset.sideSpacing) || 20,
      bottomSpacing: Number(script.dataset.bottomSpacing) || 20,
      logo: script.dataset.logo,
      mode: script.dataset.mode,
    };

    if (config.tenantId && config.brandId && config.iframeSrc) {
      ChatSDK.init(config);
    }
  }
})();
